import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { createWorkflow, fetchWorkflows, type WorkflowKind } from '@/services/logicAppsApi';
import { extractWorkflowName, type Workflow } from '@/types/logicApps';
import { telemetryService } from '@/services/telemetryService';
import { trackFunnelStep, FunnelNames } from '@/services/telemetryFunnels';

type UseCreateWorkflowOptions = {
  appInfo: { projectName: string; appName: string };
  kind?: WorkflowKind | undefined;
  /** Prefix for auto-generated names (default: 'myWorkflow' or 'myAgent' for Agent kind) */
  namePrefix?: string | undefined;
  onSuccess?: (workflowName: string) => void;
  onError?: (error: Error) => void;
};

/**
 * Generate a unique workflow name by checking existing workflows.
 * Merges cached data with fresh fetch to ensure uniqueness.
 */
export function generateUniqueName(existingWorkflows: Workflow[], prefix: string): string {
  // Extract just the workflow name (handle "appName/workflowName" format from ARM API)
  const existingNames = new Set(
    existingWorkflows.map((w) => extractWorkflowName(w.name).toLowerCase())
  );

  let counter = 1;
  while (existingNames.has(`${prefix.toLowerCase()}${counter}`)) {
    counter++;
  }

  return `${prefix}${counter}`;
}

/**
 * Merge workflows from multiple sources, deduplicating by name.
 * This ensures we catch all existing workflows even if cache is stale.
 */
function mergeWorkflows(...sources: (Workflow[] | undefined)[]): Workflow[] {
  const seen = new Map<string, Workflow>();
  for (const source of sources) {
    if (source) {
      for (const workflow of source) {
        // Use lowercase name as key to handle case-insensitive comparison
        const key = workflow.name.toLowerCase();
        if (!seen.has(key)) {
          seen.set(key, workflow);
        }
      }
    }
  }
  return Array.from(seen.values());
}

export const useCreateWorkflow = ({ appInfo, kind, namePrefix, onSuccess, onError }: UseCreateWorkflowOptions) => {
  const { getOttoToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<{ name: string }, Error, string | void>({
    mutationFn: async (providedName) => {

      // If no name provided, generate a unique name
      let workflowName = providedName;
      if (!workflowName) {
        console.log('[useCreateWorkflow] Starting unique name generation');
        console.log('[useCreateWorkflow] appInfo:', appInfo);
        console.log('[useCreateWorkflow] kind:', kind);
        console.log('[useCreateWorkflow] namePrefix:', namePrefix);

        // Get cached workflows from the query client (what's shown on dashboard)
        const cachedWorkflows = queryClient.getQueryData<Workflow[]>(['workflows', appInfo.projectName.toLowerCase(), appInfo.appName.toLowerCase()]) ?? [];
        console.log('[useCreateWorkflow] Query key used:', ['workflows', appInfo.projectName.toLowerCase(), appInfo.appName.toLowerCase()]);
        console.log('[useCreateWorkflow] Cached workflows:', cachedWorkflows);
        console.log('[useCreateWorkflow] Cached workflow names:', cachedWorkflows?.map(w => w.name));

        // Also fetch fresh data to catch any recent changes
        let freshWorkflows: Workflow[] = [];
        try {
          freshWorkflows = await fetchWorkflows({
            projectName: appInfo.projectName,
            appName: appInfo.appName,
            getToken: getOttoToken,
          });
          console.log('[useCreateWorkflow] Fresh workflows:', freshWorkflows);
          console.log('[useCreateWorkflow] Fresh workflow names:', freshWorkflows.map(w => w.name));
        } catch (err) {
          console.error('[useCreateWorkflow] Failed to fetch fresh workflows:', err);
          // Continue with just cached data
        }

        // Merge both sources to ensure we don't miss any existing workflows
        const allWorkflows = mergeWorkflows(cachedWorkflows, freshWorkflows);
        console.log('[useCreateWorkflow] Merged workflows count:', allWorkflows.length);
        console.log('[useCreateWorkflow] Merged workflow names (raw):', allWorkflows.map(w => w.name));
        console.log('[useCreateWorkflow] Merged workflow names (extracted):', allWorkflows.map(w => extractWorkflowName(w.name)));

        // Use provided prefix, or default based on kind
        const prefix = namePrefix ?? (kind === 'Agent' ? 'myAgent' : 'myWorkflow');
        console.log('[useCreateWorkflow] Using prefix:', prefix);

        workflowName = generateUniqueName(allWorkflows, prefix);
        console.log('[useCreateWorkflow] Generated name:', workflowName);
      }

      return createWorkflow({
        projectName: appInfo.projectName,
        appName: appInfo.appName,
        getToken: getOttoToken,
        workflowName,
        kind,
      });
    },
    onSuccess: (data) => {
      // Invalidate workflows query to refetch the list. The cache key in
      // useAppResource is registered with a lowercased apiId, so match that
      // exactly — otherwise prefix matching falls through and the WorkflowList
      // page keeps stale data after a create.
      queryClient.invalidateQueries({ queryKey: ['workflows', appInfo.projectName.toLowerCase(), appInfo.appName.toLowerCase()] });
      telemetryService.trackEvent('workflow.created', { workflowName: data.name });
      trackFunnelStep(FunnelNames.ONBOARDING, 'create_workflow', 4, { workflowName: data.name });
      onSuccess?.(data.name);
    },
    onError: (error: Error) => {
      telemetryService.trackEvent('workflow.create_failed', { error: error.message });
      onError?.(error);
    },
  });
};
